import type { MelodyEntityNote } from '@tunasong/schemas'
import type { VocalRange } from '../vocal/types.js'

/**
 * Range (melody toolkit M8a; spec §5.7): how much of a line sits outside the singer's range, and the octave shift that
 * would fix it. Bowie's rules (2026-10-31):
 * - The share is the time outside: note duration outside the band over all note duration. Rests count for nothing,
 *   the edges are inside, and a note is wholly in or wholly out.
 * - An offer is made only when more than half the time is outside, and only of a shift of one or two whole octaves that
 *   brings it to half or less. The whole line moves, and no shift that leaves MIDI 0–127 is considered.
 * - Range never refuses a note: nothing here clamps, folds or drops one.
 */

export const voiceTypes = ['soprano', 'alto', 'tenor', 'baritone', 'bass'] as const
export type VoiceType = (typeof voiceTypes)[number]

/**
 * Bowie's practical, untrained ranges (spec §5.7), inclusive, in SOUNDING pitch: a tenor line written in treble-8vb is
 * stored an octave below where it is written. Narrower on purpose than classical ranges.
 */
export const voiceTypeBands: Record<VoiceType, VocalRange> = {
  soprano: { low: 60, high: 81 },
  alto: { low: 53, high: 74 },
  tenor: { low: 48, high: 69 },
  baritone: { low: 45, high: 65 },
  bass: { low: 40, high: 60 },
}

/** The offer is made only above this share outside, and only of a shift that brings it to this or less */
const offerShare = 0.5
const shifts = [-2, -1, 1, 2] as const
const lowestMidi = 0
const highestMidi = 127

const isInside = (midi: number, range: VocalRange) => midi >= range.low && midi <= range.high

/** The share of the line's time outside the range, 0 to 1; 0 for no notes */
export const outsideShare = (notes: MelodyEntityNote[], range: VocalRange): number => {
  const total = notes.reduce((sum, note) => sum + note.duration, 0)
  if (total <= 0) {
    return 0
  }
  const outside = notes.reduce((sum, note) => (isInside(note.midi, range) ? sum : sum + note.duration), 0)
  return outside / total
}

export interface RangeOffer {
  /** Whole octaves, up (positive) or down */
  octaves: number
  /** The whole line, moved */
  notes: MelodyEntityNote[]
  /** What it leaves outside */
  share: number
}

export interface RangeFit {
  share: number
  offer: RangeOffer | null
}

const centreOf = (notes: MelodyEntityNote[]) => {
  const midis = notes.map(note => note.midi)
  return (Math.min(...midis) + Math.max(...midis)) / 2
}

/**
 * The share outside, and the octave offer. Among the shifts that stay in MIDI 0–127, the winner leaves the least time
 * outside; then it is the smaller shift; then the one whose span centre is nearest the band's; then down, as strain at
 * the top is worse than at the bottom. It is offered only when the line is more than half outside and it brings that to
 * half or less.
 */
export const rangeFit = (notes: MelodyEntityNote[], range: VocalRange): RangeFit => {
  const share = outsideShare(notes, range)
  if (share <= offerShare) {
    return { share, offer: null }
  }
  const bandCentre = (range.low + range.high) / 2
  const candidates = shifts.flatMap(octaves => {
    const moved = notes.map(note => ({ ...note, midi: note.midi + octaves * 12 }))
    const isPlayable = moved.every(note => note.midi >= lowestMidi && note.midi <= highestMidi)
    return isPlayable
      ? [{ octaves, notes: moved, share: outsideShare(moved, range), distance: Math.abs(centreOf(moved) - bandCentre) }]
      : []
  })
  const best = candidates.reduce<(typeof candidates)[number] | undefined>((winner, candidate) => {
    if (!winner) {
      return candidate
    }
    const order = [
      candidate.share - winner.share,
      Math.abs(candidate.octaves) - Math.abs(winner.octaves),
      candidate.distance - winner.distance,
      candidate.octaves - winner.octaves,
    ]
    const decider = order.find(difference => Math.abs(difference) > 1e-9) ?? 0
    return decider < 0 ? candidate : winner
  }, undefined)
  if (!best || best.share > offerShare) {
    return { share, offer: null }
  }
  return { share, offer: { octaves: best.octaves, notes: best.notes, share: best.share } }
}
